import { useEffect, useRef } from 'react';
import { getAuthToken, isTokenExpired, forceLogout, decodeToken } from '../helpers/auth-helpers';

/**
 * Custom hook to monitor token expiration and automatically logout users
 * @param {number} checkInterval - Interval in milliseconds to check token expiration (default: 30 seconds)
 */
export const useTokenExpiration = (checkInterval = 30000) => {
  const intervalRef = useRef(null);
  const warningShownRef = useRef(false);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = getAuthToken();
      
      if (!token) {
        return;
      }

      if (isTokenExpired(token)) {
        console.log('Token expired, logging out user...');
        forceLogout();
        return;
      }

      // Check if token will expire in the next 5 minutes and show warning
      const decoded = decodeToken(token);
      if (decoded && decoded.exp) {
        const expirationTime = decoded.exp * 1000; // Convert to milliseconds
        const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
        const timeUntilExpiration = expirationTime - Date.now();

        if (timeUntilExpiration <= fiveMinutes && timeUntilExpiration > 0 && !warningShownRef.current) {
          console.warn('Token will expire in less than 5 minutes');
          warningShownRef.current = true;
          
          // Optional: Show user notification
          if (window.toast) {
            window.toast.warning('Your session will expire soon. Please save your work.');
          }
        }

        // Reset warning if token is refreshed
        if (timeUntilExpiration > fiveMinutes) {
          warningShownRef.current = false;
        }
      }
    };

    // Check immediately
    checkTokenExpiration();

    // Set up interval to check periodically
    intervalRef.current = setInterval(checkTokenExpiration, checkInterval);

    // Cleanup interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkInterval]);

  // Also check on page focus (when user comes back to the tab)
  useEffect(() => {
    const handleFocus = () => {
      const token = getAuthToken();
      if (token && isTokenExpired(token)) {
        console.log('Token expired while tab was inactive, logging out user...');
        forceLogout();
      }
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);
};

/**
 * Hook to get token expiration information
 * @returns {object} Object containing token expiration details
 */
export const useTokenInfo = () => {
  const token = getAuthToken();
  
  if (!token) {
    return {
      isValid: false,
      isExpired: true,
      expiresAt: null,
      timeUntilExpiration: null
    };
  }

  const decoded = decodeToken(token);
  const isExpired = isTokenExpired(token);
  
  let expiresAt = null;
  let timeUntilExpiration = null;
  
  if (decoded && decoded.exp) {
    expiresAt = new Date(decoded.exp * 1000);
    timeUntilExpiration = decoded.exp * 1000 - Date.now();
  }

  return {
    isValid: !isExpired,
    isExpired,
    expiresAt,
    timeUntilExpiration: timeUntilExpiration > 0 ? timeUntilExpiration : 0
  };
};

export default useTokenExpiration;
